/**
 * Оформление: что доступно для этой корзины, сколько это стоит и как заказ
 * создаётся.
 *
 * Считает сервер: он знает остатки по аптекам, лимит списания баллов и то,
 * подключён ли провайдер оплаты. Недоступные способы приходят с причиной —
 * её и показываем, а не прячем сам способ (docs/frontend/README.md).
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { components } from '@dorixona/api'
import { useApi } from '../../api/context'
import { keys } from '../../api/keys'
import { useGeocoderKeySource } from '../../api/mapsKey'
import { toLine, type CartLine } from '../cart/api'
import { geocoderLang, reverseGeocode, searchAddresses } from './geocoder'

type OrderDto = components['schemas']['Order']

export type ShipMethod = 'pickup' | 'delivery'
export type PayMethod =
  | 'card'
  | 'click'
  | 'payme'
  | 'uzum'
  // Приложения, которые приходят только со страницей Rahmat (Multicard, R-110).
  | 'anorbank'
  | 'oson'
  | 'alif'
  | 'xazna'
  | 'beepul'
  | 'trastpay'
  | 'saved_card'
  | 'pay_at_pharmacy'

export interface PickupStore {
  storeId: number
  name: string
  district: string
  address: string
  lat: number | null
  lng: number | null
  /** Метров от покупателя; null — сервер не знал точки или у аптеки её нет. */
  distanceM: number | null
  openNow: boolean
  /** Аптека собирает всю корзину; иначе `missing` строк не хватает. */
  canFulfil: boolean
  missing: number
}

export interface DeliveryOption {
  available: boolean
  /** Почему нельзя: нет провайдера, рецепт в корзине, некому собрать. */
  reason: string
  fee: number
  storeId: number
}

/** Куда везти: подпись для людей и точка, по которой сервер ищет аптеку и курьера. */
export interface DeliveryAddress {
  label: string
  lat: number
  lng: number
  /** Сохранённый адрес: сервер берёт его номером, не перепечатывая (F-33). */
  id?: number
  /** Его ярлык — «Дом», «Работа». */
  tag?: string
}

/**
 * Куда везти — в теле оформления: сохранённый адрес номером, новый — подписью
 * и точкой. Ярлык и номер внутрь адреса не кладём: это не часть адреса.
 */
export function addressBody(address: DeliveryAddress | null) {
  if (!address) return {}
  if (address.id) return { address_id: address.id }
  return { address: { label: address.label, lat: address.lat, lng: address.lng } }
}

export interface PayOption {
  code: PayMethod
  available: boolean
  reason: string
}

export interface CheckoutOptions {
  hasRx: boolean
  pickup: PickupStore[]
  delivery: DeliveryOption
  pay: PayOption[]
}

interface OptionsDto {
  has_rx?: boolean
  pickup?: {
    store_id?: number
    name?: string
    district?: string
    address?: string
    lat?: number | null
    lng?: number | null
    distance_m?: number | null
    open_now?: boolean
    can_fulfil?: boolean
    missing?: number
  }[]
  delivery?: { available?: boolean; reason?: string; fee?: number; store_id?: number | null }
  pay_methods?: { code?: PayMethod; available?: boolean; reason?: string }[]
}

export function toOptions(dto: OptionsDto): CheckoutOptions {
  return {
    hasRx: dto.has_rx ?? false,
    pickup: (dto.pickup ?? []).map((p) => ({
      storeId: p.store_id ?? 0,
      name: p.name ?? '',
      district: p.district ?? '',
      address: p.address ?? '',
      lat: p.lat ?? null,
      lng: p.lng ?? null,
      distanceM: p.distance_m ?? null,
      openNow: p.open_now ?? false,
      canFulfil: p.can_fulfil ?? false,
      missing: p.missing ?? 0,
    })),
    delivery: {
      available: dto.delivery?.available ?? false,
      reason: dto.delivery?.reason ?? '',
      fee: dto.delivery?.fee ?? 0,
      storeId: dto.delivery?.store_id ?? 0,
    },
    pay: (dto.pay_methods ?? []).map((m) => ({
      code: m.code ?? 'pay_at_pharmacy',
      available: m.available ?? false,
      reason: m.reason ?? '',
    })),
  }
}

/**
 * Без адреса сервер не знает, какая аптека ближе и сколько возьмёт курьер, —
 * доставка приходит с причиной `address_required`. Где покупатель (`here`) —
 * сервер ставит аптеки самовывоза ближними вперёд и пишет до них метры; без
 * этой точки считает от точки адреса. Адрес или точка меняются — варианты
 * пересчитываются; пока идёт расчёт, на экране прежние, а не скелет.
 */
export function useCheckoutOptions(
  enabled: boolean,
  address: DeliveryAddress | null,
  here: { lat: number; lng: number } | null = null,
) {
  const api = useApi()
  const body = { ...addressBody(address), ...(here ? { lat: here.lat, lng: here.lng } : {}) }
  return useQuery({
    queryKey: [...keys.checkoutOptions, address, here],
    queryFn: async () => toOptions(await api.post<OptionsDto>('/checkout/options', body)),
    enabled,
    placeholderData: keepPreviousData,
  })
}

export interface QuoteInput {
  ship: ShipMethod
  storeId: number
  pay: PayMethod
  /** Сколько баллов покупатель хочет списать; сервер сам ограничит лимитом. */
  spend: number
  /** Доставка: куда везти. Самовывозу не нужен. */
  address: DeliveryAddress | null
}

export interface Quote {
  lines: CartLine[]
  goods: number
  deliveryFee: number
  /** Потолок списания: остаток на карте и лимит сети (R-23…R-25). */
  maxSpend: number
  spend: number
  cashback: number
  total: number
}

interface QuoteDto {
  lines?: components['schemas']['CartLine'][]
  goods_total?: number
  delivery_fee?: number
  max_spend?: number
  spend?: number
  cashback?: number
  total_to_pay?: number
}

export function toQuote(dto: QuoteDto): Quote {
  return {
    lines: (dto.lines ?? []).map(toLine),
    goods: dto.goods_total ?? 0,
    deliveryFee: dto.delivery_fee ?? 0,
    maxSpend: dto.max_spend ?? 0,
    spend: dto.spend ?? 0,
    cashback: dto.cashback ?? 0,
    total: dto.total_to_pay ?? 0,
  }
}

function toBody(input: QuoteInput) {
  return {
    ship_method: input.ship,
    store_id: input.ship === 'pickup' ? input.storeId : null,
    pay_method: input.pay,
    spend_cashback: input.spend,
    ...(input.ship === 'delivery' ? addressBody(input.address) : {}),
  }
}

/** Заказу нужна ещё и карта: какую из сохранённых заблокировать на сумму. */
function toOrderBody(input: PlaceInput) {
  return {
    ...toBody(input),
    card_id: input.pay === 'saved_card' ? input.cardId : undefined,
  }
}

export function useQuote(input: QuoteInput, enabled: boolean) {
  const api = useApi()
  return useQuery({
    queryKey: keys.quote(toBody(input)),
    queryFn: async () => toQuote(await api.post<QuoteDto>('/checkout/quote', toBody(input))),
    enabled,
    // Пока сервер пересчитывает, на экране прежний итог, а не пустое место:
    // иначе блок баллов пропадал бы на время расчёта.
    placeholderData: keepPreviousData,
  })
}

export interface PlacedOrder {
  id: number
  number: number
  /** Онлайн-оплата: страница, на которую уходит покупатель. */
  checkoutUrl: string
}

export interface PlaceInput extends QuoteInput {
  /** Сохранённая карта для `saved_card`; для остальных способов не нужна. */
  cardId?: number
}

/**
 * Создание заказа. `Idempotency-Key` обязателен: повтор с тем же ключом
 * вернёт уже созданный заказ, а не второй такой же (R-27).
 */
export function usePlaceOrder() {
  const api = useApi()
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ input, key }: { input: PlaceInput; key: string }) => {
      const done = await api.post<{ order?: OrderDto; payment?: { checkout_url?: string } }>(
        '/orders',
        toOrderBody(input),
        { headers: { 'Idempotency-Key': key } },
      )
      return {
        id: done.order?.id ?? 0,
        number: done.order?.number ?? 0,
        checkoutUrl: done.payment?.checkout_url ?? '',
      } satisfies PlacedOrder
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: keys.cart })
      await client.invalidateQueries({ queryKey: keys.orders })
      await client.invalidateQueries({ queryKey: keys.loyalty })
    },
  })
}

/**
 * Адреса по набранному тексту — геокодер Яндекса, от трёх букв. Без точки
 * адрес бесполезен: по нему не найти аптеку, — такие ответы геокодер отсеивает.
 */
export function useAddressSuggest(query: string, language: string) {
  const q = query.trim()
  const lang = geocoderLang(language)
  const keySource = useGeocoderKeySource()
  return useQuery({
    queryKey: ['geo', 'suggest', q, lang],
    queryFn: () => searchAddresses(keySource, q, lang),
    enabled: q.length >= 3,
    staleTime: 60_000,
  })
}

/** Улица и дом у точки, которую нажали на карте. */
export function useReverseAddress(point: { lat: number; lng: number } | null, language: string) {
  const lang = geocoderLang(language)
  const keySource = useGeocoderKeySource()
  return useQuery({
    queryKey: ['geo', 'reverse', point?.lat, point?.lng, lang],
    queryFn: async () => {
      const at = point as { lat: number; lng: number }
      // Рядом нет дома (парк, пустырь) — подписываем координатами, а не пустотой.
      return (await reverseGeocode(keySource, at, lang)) || `${at.lat}, ${at.lng}`
    },
    enabled: point !== null,
    staleTime: 60_000,
  })
}
